import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import adminApi, { AdminBannerDto, CreateBannerDto, UpdateBannerDto } from '../../services/adminApi';

const AdminBannersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<AdminBannerDto | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingBanner, setDeletingBanner] = useState<AdminBannerDto | null>(null);

  // Fetch banners
  const { data: banners, isLoading, error } = useQuery({
    queryKey: ['admin-banners'],
    queryFn: () => adminApi.getBanners(),
  });

  // Create banner mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateBannerDto) => adminApi.createBanner(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-banners'] });
      setIsCreateModalOpen(false);
    },
  });

  // Update banner mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBannerDto }) => adminApi.updateBanner(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-banners'] });
      setIsEditModalOpen(false);
      setEditingBanner(null);
    },
  });

  // Delete banner mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteBanner(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-banners'] });
      setIsDeleteModalOpen(false);
      setDeletingBanner(null);
    },
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: (id: string) => adminApi.toggleBannerActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-banners'] });
    },
  });

  const getBannerPositionName = (position: number): string => {
    const positions = {
      0: 'منوی جستجو',
      1: 'صفحه اصلی - بنر اصلی',
      2: 'صفحه اصلی - نوار کناری',
      3: 'صفحه جستجو - بالا',
      4: 'صفحه جستجو - نوار کناری',
      5: 'جزئیات آگهی - بالا',
      6: 'جزئیات آگهی - نوار کناری',
      7: 'پروفایل کاربر - بالا',
      8: 'فوتر'
    };
    return positions[position as keyof typeof positions] || 'نامشخص';
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4" dir="rtl">
        <p className="text-red-600">خطا در بارگذاری بنرها</p>
      </div>
    );
  }

  return (
    <div className="p-6" dir="rtl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">مدیریت بنرها</h1>
            <p className="text-gray-600 mt-2">مدیریت بنرهای تبلیغاتی در موقعیت‌های مختلف سایت</p>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition duration-200 flex items-center gap-2 gap-2-reverse"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            افزودن بنر جدید
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
            </div>
            <div className="mr-4">
              <h3 className="text-lg font-semibold text-gray-900">{banners?.data?.length || 0}</h3>
              <p className="text-gray-600">کل بنرها</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="mr-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {banners?.data?.filter(banner => banner.isActive).length || 0}
              </h3>
              <p className="text-gray-600">بنرهای فعال</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
            <div className="mr-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {banners?.data?.filter(banner => !banner.isActive).length || 0}
              </h3>
              <p className="text-gray-600">بنرهای غیرفعال</p>
            </div>
          </div>
        </div>
      </div>

      {/* Banners Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">لیست بنرها</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">تصویر</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">عنوان</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">موقعیت</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وضعیت</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">ترتیب نمایش</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">تاریخ ایجاد</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">عملیات</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {banners?.data?.map((banner) => (
                <tr key={banner.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="w-16 h-12 rounded-lg shadow-sm overflow-hidden bg-gray-100 flex items-center justify-center">
                      <img
                        src={banner.imageUrl}
                        alt={banner.title}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = 'https://via.placeholder.com/64x48?text=No+Image';
                        }}
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{banner.title}</div>
                    <div className="text-sm text-gray-500 truncate max-w-xs">{banner.description}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {getBannerPositionName(banner.position)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => toggleActiveMutation.mutate(banner.id)}
                      disabled={toggleActiveMutation.isPending}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        banner.isActive
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      } transition-colors duration-200`}
                    >
                      {banner.isActive ? 'فعال' : 'غیرفعال'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {banner.sortOrder}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(banner.createdDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <button
                        onClick={() => {
                          setEditingBanner(banner);
                          setIsEditModalOpen(true);
                        }}
                        className="text-indigo-600 hover:text-indigo-900 transition-colors duration-200"
                      >
                        ویرایش
                      </button>
                      <button
                        onClick={() => {
                          setDeletingBanner(banner);
                          setIsDeleteModalOpen(true);
                        }}
                        className="text-red-600 hover:text-red-900 transition-colors duration-200"
                      >
                        حذف
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(!banners?.data || banners.data.length === 0) && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">هیچ بنری در دسترس نیست</h3>
            <p className="mt-1 text-sm text-gray-500">با ایجاد یک بنر جدید شروع کنید.</p>
            <div className="mt-6">
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <svg className="ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                افزودن بنر جدید
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Banner Modal */}
      {isCreateModalOpen && (
        <BannerModal
          title="افزودن بنر جدید"
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          isLoading={createMutation.isPending}
        />
      )}

      {/* Edit Banner Modal */}
      {isEditModalOpen && editingBanner && (
        <BannerModal
          title="ویرایش بنر"
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingBanner(null);
          }}
          onSubmit={(data) => updateMutation.mutate({ id: editingBanner.id, data })}
          isLoading={updateMutation.isPending}
          initialData={editingBanner}
        />
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && deletingBanner && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" dir="rtl">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 mt-5">حذف بنر</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  آیا مطمئن هستید که می‌خواهید بنر "{deletingBanner.title}" را حذف کنید؟
                  این عمل قابل بازگشت نیست.
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <div className="flex justify-center space-x-4 space-x-reverse">
                  <button
                    onClick={() => {
                      setIsDeleteModalOpen(false);
                      setDeletingBanner(null);
                    }}
                    className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md shadow-sm hover:bg-gray-600"
                  >
                    لغو
                  </button>
                  <button
                    onClick={() => deleteMutation.mutate(deletingBanner.id)}
                    disabled={deleteMutation.isPending}
                    className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 disabled:opacity-50"
                  >
                    {deleteMutation.isPending ? 'در حال حذف...' : 'حذف'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Banner Modal Component
interface BannerModalProps {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateBannerDto | UpdateBannerDto) => void;
  isLoading: boolean;
  initialData?: AdminBannerDto;
}

const BannerModal: React.FC<BannerModalProps> = ({
  title,
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  initialData
}) => {
  const [formData, setFormData] = useState<CreateBannerDto>({
    title: initialData?.title || '',
    description: initialData?.description || '',
    imageUrl: initialData?.imageUrl || '',
    linkUrl: initialData?.linkUrl || '',
    position: initialData?.position || 0,
    isActive: initialData?.isActive ?? true,
    sortOrder: initialData?.sortOrder || 1,
    startDate: initialData?.startDate || '',
    endDate: initialData?.endDate || '',
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string>('');

  const bannerPositions = [
    { value: 0, label: 'منوی جستجو', dimensions: '800x250 pixels', description: 'بنر بزرگ برای نمایش در منوی جستجو' },
    { value: 1, label: 'صفحه اصلی - بنر اصلی', dimensions: '1200x300 pixels', description: 'بنر بزرگ در بالای صفحه اصلی' },
    { value: 2, label: 'صفحه اصلی - نوار کناری', dimensions: '300x250 pixels', description: 'بنر مربعی در نوار کناری' },
    { value: 3, label: 'صفحه جستجو - بالا', dimensions: '728x90 pixels', description: 'بنر افقی در بالای صفحه جستجو' },
    { value: 4, label: 'صفحه جستجو - نوار کناری', dimensions: '300x250 pixels', description: 'بنر مربعی در نوار کناری صفحه جستجو' },
    { value: 5, label: 'جزئیات آگهی - بالا', dimensions: '728x90 pixels', description: 'بنر افقی در بالای صفحه جزئیات آگهی' },
    { value: 6, label: 'جزئیات آگهی - نوار کناری', dimensions: '300x250 pixels', description: 'بنر مربعی در نوار کناری جزئیات آگهی' },
    { value: 7, label: 'پروفایل کاربر - بالا', dimensions: '728x90 pixels', description: 'بنر افقی در بالای صفحه پروفایل' },
    { value: 8, label: 'فوتر', dimensions: '728x90 pixels', description: 'بنر افقی در فوتر سایت' },
  ];

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('اندازه فایل نباید از ۵ مگابایت بیشتر باشد');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('فرمت فایل نامعتبر است. فرمت‌های مجاز: jpg, jpeg, png, gif, webp');
      return;
    }

    setSelectedFile(file);
    setUploadError('');
    setIsUploading(true);

    try {
      const response = await adminApi.uploadBannerImage(file);
      const imageUrl = response.data.imageUrl;
      
      // Update the form data with the uploaded image URL
      setFormData({ ...formData, imageUrl });
      setIsUploading(false);
    } catch (error: any) {
      setUploadError(error.response?.data?.message || 'خطا در آپلود تصویر');
      setIsUploading(false);
      setSelectedFile(null);
    }
  };

  const getSelectedPositionDetails = () => {
    return bannerPositions.find(pos => pos.value === formData.position);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" dir="rtl">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">عنوان بنر *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
                maxLength={200}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">موقعیت نمایش *</label>
              <select
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              >
                {bannerPositions.map((position) => (
                  <option key={position.value} value={position.value}>
                    {position.label}
                  </option>
                ))}
              </select>
              
              {/* Display recommended dimensions for selected position */}
              {getSelectedPositionDetails() && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-blue-600 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-blue-800">
                        اندازه پیشنهادی: <span className="font-semibold">{getSelectedPositionDetails()?.dimensions}</span>
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        {getSelectedPositionDetails()?.description}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">توضیحات *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
              maxLength={500}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">تصویر بنر *</label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                {!formData.imageUrl ? (
                  <>
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none"
                      >
                        <span>انتخاب فایل</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          accept="image/*"
                          onChange={handleFileSelect}
                          disabled={isUploading}
                        />
                      </label>
                      <p className="pr-1">یا بکشید و رها کنید</p>
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF, WEBP تا ۵ مگابایت</p>
                  </>
                ) : (
                  <div className="space-y-2">
                    <div className="mx-auto h-32 w-full max-w-sm rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                      <img
                        src={formData.imageUrl}
                        alt="Preview"
                        className="max-w-full max-h-full object-contain rounded-lg"
                      />
                    </div>
                    <div className="flex justify-center space-x-2 space-x-reverse">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, imageUrl: '' })}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        حذف تصویر
                      </button>
                      <label
                        htmlFor="file-upload-replace"
                        className="text-sm text-primary-600 hover:text-primary-800 cursor-pointer"
                      >
                        تغییر تصویر
                        <input
                          id="file-upload-replace"
                          name="file-upload-replace"
                          type="file"
                          className="sr-only"
                          accept="image/*"
                          onChange={handleFileSelect}
                          disabled={isUploading}
                        />
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {isUploading && (
              <div className="mt-2 text-sm text-blue-600">در حال آپلود...</div>
            )}
            
            {uploadError && (
              <div className="mt-2 text-sm text-red-600">{uploadError}</div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">لینک مقصد</label>
            <input
              type="url"
              value={formData.linkUrl}
              onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="https://example.com"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ترتیب نمایش</label>
              <input
                type="number"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 1 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                min="1"
                max="100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">تاریخ شروع</label>
              <input
                type="datetime-local"
                value={formData.startDate ? new Date(formData.startDate).toISOString().slice(0, 16) : ''}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">تاریخ پایان</label>
              <input
                type="datetime-local"
                value={formData.endDate ? new Date(formData.endDate).toISOString().slice(0, 16) : ''}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="mr-2 block text-sm text-gray-900">
              فعال
            </label>
          </div>

          <div className="flex justify-end space-x-3 space-x-reverse pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              لغو
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {isLoading ? 'در حال ذخیره...' : (initialData ? 'به‌روزرسانی' : 'افزودن')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminBannersPage; 